import { filter, map, startWith, type Observable } from 'rxjs';
import { toPersistentStream } from '@ardoq/rxbeach';
import type { ApiComponentTypeOverviewRow } from '@ardoq/api-types';
import { isLoaded, LOADING, type LoadState } from '@ardoq/common-helpers';
import { componentTypeOverview$ } from 'streams/componentTypeOverview$';

const APPLICATION_TYPE_NAME = 'application';

const toComparableName = (row: ApiComponentTypeOverviewRow): string =>
  row.name.trim().toLowerCase();

const isApplicationType = (row: ApiComponentTypeOverviewRow): boolean =>
  toComparableName(row) === APPLICATION_TYPE_NAME;

const isApplicationTypeVariant = (row: ApiComponentTypeOverviewRow): boolean =>
  toComparableName(row).startsWith(APPLICATION_TYPE_NAME);

const byComponentCountDescending = (
  a: ApiComponentTypeOverviewRow,
  b: ApiComponentTypeOverviewRow
): number => b.componentCount - a.componentCount;

const mostPopulated = (
  rows: ApiComponentTypeOverviewRow[]
): ApiComponentTypeOverviewRow | undefined =>
  rows.toSorted(byComponentCountDescending)[0];

export type FallbackComponentTypeForTour = {
  name: string;
  ids: string[];
};

/**
 * The type to fall back to, most specific first: Application itself, else the
 * biggest type named after it ("Application Group", "Application Service"), else
 * the most populated one. Only types that hold components qualify, since the tour
 * has to open one of them.
 */
export const deriveFallbackComponentTypeForTour = (
  overview: ApiComponentTypeOverviewRow[]
): FallbackComponentTypeForTour | null => {
  const populated = overview.filter(row => row.componentCount > 0);
  const name = (
    populated.find(isApplicationType) ??
    mostPopulated(populated.filter(isApplicationTypeVariant)) ??
    mostPopulated(populated)
  )?.name;

  return name
    ? {
        name,
        ids: populated.filter(row => row.name === name).map(row => row.id),
      }
    : null;
};

export type FallbackComponentTypeForTourState =
  LoadState<FallbackComponentTypeForTour | null>;

export const fallbackComponentTypeForTour$ =
  toPersistentStream<FallbackComponentTypeForTourState>(
    'fallbackComponentTypeForTour$',
    componentTypeOverview$.pipe(
      filter(isLoaded),
      map(deriveFallbackComponentTypeForTour),
      startWith(LOADING)
    ),
    LOADING
  );

export const loadedFallbackComponentTypeForTour$: Observable<FallbackComponentTypeForTour | null> =
  fallbackComponentTypeForTour$.pipe(filter(isLoaded));

export const toFallbackComponentTypeForTour = (
  state: FallbackComponentTypeForTourState
): FallbackComponentTypeForTour | null => (isLoaded(state) ? state : null);
